import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Search, Refresh, GetApp, ArrowBack, Close } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar, InputAdornment, TextField } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';
import { formatDate as fmtDate } from '../../../utils/dateUtils';

const PRIMARY = '#263078';
const PRIMARY_MID = '#3949ab';
const PRIMARY_LT = '#e8eaf6';

const fmt = (n) =>
  `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmt2Date = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const voucherColor = (type) => {
  switch ((type || '').toLowerCase()) {
    case 'sale':           return { bg: '#e8f5e9', color: '#2e7d32' };
    case 'purchase':       return { bg: '#fff3e0', color: '#e65100' };
    case 'receipt':        return { bg: '#e3f2fd', color: '#1565c0' };
    case 'payment':        return { bg: '#fce4ec', color: '#c62828' };
    case 'journal':        return { bg: '#f3e5f5', color: '#6a1b9a' };
    case 'salereturn':     return { bg: '#fce4ec', color: '#c62828' };
    case 'purchasereturn': return { bg: '#fff8e1', color: '#f57f17' };
    default:               return { bg: '#f5f5f5', color: '#616161' };
  }
};

const iconBtn = {
  color: 'rgba(255,255,255,0.85)',
  '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.15)' },
  borderRadius: '6px', p: '4px',
};
const th  = { padding: '6px 8px', textAlign: 'left', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '2px solid rgba(255,255,255,0.2)' };
const td  = { padding: '5px 8px', fontSize: 12, color: '#374151', whiteSpace: 'nowrap' };
const tdR = { ...td, textAlign: 'right' };

const DayBookReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [fetched, setFetched]   = useState(false);
  const [search, setSearch]     = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));

  const fetchReport = async (cId = companyId, bId = branchId, tok = token) => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    if (!cId || !bId || !tok) { setError('Authentication data missing'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetchData(
        'FinancialStatementsReportApi',
        `/DayBookSummaryReport/${companyId}/${branchId}/${fromDate}/${toDate}`,
        tok
      );
      const rows = res.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error('DayBookReport API error:', err);
      setData([]);
      setFetched(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token) fetchReport(companyId, branchId, token);
  }, [companyId, branchId, token]); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(r =>
      (r.VoucherType || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const totalDebit  = filtered.reduce((s, r) => s + (parseFloat(r.TotalDebit)  || 0), 0);
  const totalCredit = filtered.reduce((s, r) => s + (parseFloat(r.TotalCredit) || 0), 0);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'DayBookReport', 0);
    if (!filtered.length) return;
    const cols = ['#', 'Date', 'Voucher Type', 'Voucher Count', 'Total Debit', 'Total Credit'];
    const rows = filtered.map((r, i) => [
      i + 1, fmtDate(r.Date), r.VoucherType || '',
      r.VoucherCount || 0, parseFloat(r.TotalDebit) || 0, parseFloat(r.TotalCredit) || 0,
    ]);

      await exportStyledExcel({
          reportTitle: 'DAY BOOK REPORT',
          sheetName: 'Day Book',
          fileName: `DayBook_${fromDate}_${toDate}.xlsx`,
          companyName,
          username,
          fromDate,
          toDate,
          headers: cols,
          rows,
          totalsRow: ['', '', '', 'Total', totalDebit, totalCredit],
          columnWidths: [5,14,18,14,16,16],
      });
  };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 8, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading day book..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '4px 10px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #1a237e 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Day Book</span>
          <div style={{ flex: 1, minWidth: 4 }} />
          {searchOpen ? (
            <>
              <TextField autoFocus size="small" placeholder="Search voucher, ledger..."
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: 'rgba(255,255,255,0.6)' }} /></InputAdornment>,
                  endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')} sx={{ color: 'rgba(255,255,255,0.6)', p: '2px' }}><Close sx={{ fontSize: 14 }} /></IconButton></InputAdornment> : null,
                }}
                sx={{ flexShrink: 0, width: 190, '& .MuiOutlinedInput-root': { height: 29, fontSize: 12, borderRadius: '8px', color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.4)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.7)' } }, '& input::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 } }}
              />
              <Tooltip title="Close" arrow><IconButton size="small" onClick={() => { setSearchOpen(false); setSearch(''); }} sx={iconBtn}><Close sx={{ fontSize: 16 }} /></IconButton></Tooltip>
            </>
          ) : (
            <Tooltip title="Search" arrow><IconButton size="small" onClick={() => setSearchOpen(true)} sx={iconBtn}><Search sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          )}
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate}   onChange={e => setToDate(e.target.value)}   style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow><IconButton onClick={() => fetchReport()} disabled={loading} size="small" sx={iconBtn}><Refresh sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          <Tooltip title="Export Excel" arrow><IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}><GetApp sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        </div>

        {/* Table */}
        <div className="report-table-scroll" style={{ overflowX: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 110px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 850 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: PRIMARY_MID, color: 'white' }}>
                <th style={th}>#</th>
                <th style={th}>Date</th>
                <th style={{ ...th, textAlign: 'center' }}>Voucher Type</th>
                <th style={{ ...th, textAlign: 'center' }}>Count</th>
                <th style={{ ...th, textAlign: 'right' }}>Total Debit</th>
                <th style={{ ...th, textAlign: 'right' }}>Total Credit</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>
                  No records found
                </td></tr>
              ) : (
                filtered.map((r, i) => {
                  const vc = voucherColor(r.VoucherType);
                  return (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT, borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ ...td, color: '#94a3b8' }}>{i + 1}</td>
                      <td style={{ ...td, color: '#64748b' }}>{fmtDate(r.Date)}</td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: vc.bg, color: vc.color, whiteSpace: 'nowrap', display: 'inline-block' }}>
                          {r.VoucherType || '-'}
                        </span>
                      </td>
                      <td style={{ ...td, textAlign: 'center', fontWeight: 600, color: '#374151' }}>{r.VoucherCount || 0}</td>
                      <td style={{ ...tdR, color: (parseFloat(r.TotalDebit) || 0) > 0 ? '#1565c0' : '#94a3b8', fontWeight: (parseFloat(r.TotalDebit) || 0) > 0 ? 600 : 400 }}>
                        {(parseFloat(r.TotalDebit) || 0) > 0 ? fmt(r.TotalDebit) : '-'}
                      </td>
                      <td style={{ ...tdR, color: (parseFloat(r.TotalCredit) || 0) > 0 ? '#2e7d32' : '#94a3b8', fontWeight: (parseFloat(r.TotalCredit) || 0) > 0 ? 600 : 400 }}>
                        {(parseFloat(r.TotalCredit) || 0) > 0 ? fmt(r.TotalCredit) : '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ background: PRIMARY_MID, color: '#fff', fontWeight: 700 }}>
                  <td colSpan={4} style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#fff' }}>Grand Total</td>
                  <td style={{ ...tdR, fontWeight: 700, color: '#90caf9' }}>{fmt(totalDebit)}</td>
                  <td style={{ ...tdR, fontWeight: 700, color: '#a5d6a7' }}>{fmt(totalCredit)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default DayBookReport;
